import { useNavigate } from 'react-router-dom';
import { FlaskConical, Beaker, ArrowRight, LogOut } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useProgress } from '@/context/ProgressContext';
import { COURSE_LIST } from '@/config/courses.config';
import type { Course } from '@/types';
import Watermark from '@/components/ui/Watermark';
import CopyrightFooter from '@/components/ui/CopyrightFooter';

function CourseCard({ course, progress, onSelect }: { course: Course; progress: number; onSelect: () => void }) {
  const Icon = course.id === 'chem2131' ? Beaker : FlaskConical;

  return (
    <div className="course-card" onClick={onSelect}>
      <div className="course-icon">
        <Icon size={48} />
      </div>
      <h3 className="course-code">{course.code}</h3>
      <div className="course-divider" />
      <p className="course-name">{course.fullName}</p>
      <p className="course-description">{course.description}</p>
      <div className="course-stats">
        <span>{course.experimentCount} Experiments</span>
        <span className="progress-badge">Progress: {progress}%</span>
      </div>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <button className="enter-btn">
        Enter Lab <ArrowRight size={18} />
      </button>

      <style>{`
        .course-card {
          background: var(--bg-surface, #2C2C2C);
          border: 1px solid var(--border, #4A4A4A);
          border-radius: 12px;
          padding: 24px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 320px;
        }
        .course-card:hover {
          border-color: var(--primary, #FF5A36);
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 90, 54, 0.2);
        }
        .course-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, var(--primary, #FF5A36), var(--primary-dark, #E04420));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          color: white;
        }
        .course-code {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary, #FFFFFF);
          margin: 0 0 8px 0;
        }
        .course-divider {
          width: 60px;
          height: 3px;
          background: var(--primary, #FF5A36);
          margin: 8px 0 16px 0;
        }
        .course-name {
          font-size: 14px;
          color: var(--text-secondary, #CCCCCC);
          margin: 0 0 8px 0;
          font-weight: 500;
        }
        .course-description {
          font-size: 13px;
          color: var(--text-muted, #999999);
          margin: 0 0 16px 0;
          line-height: 1.5;
        }
        .course-stats {
          display: flex;
          gap: 16px;
          font-size: 12px;
          color: var(--text-secondary, #CCCCCC);
          margin-bottom: 12px;
        }
        .progress-badge {
          color: var(--secondary, #00A86B);
          font-weight: 600;
        }
        .progress-bar {
          width: 100%;
          height: 6px;
          background: var(--bg-dark, #1A1A1A);
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 20px;
        }
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--secondary, #00A86B), var(--secondary-dark, #2E8B57));
          border-radius: 3px;
          transition: width 0.3s ease;
        }
        .enter-btn {
          background: var(--primary, #FF5A36);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
        }
        .enter-btn:hover {
          background: var(--primary-dark, #E04420);
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
}

export default function CourseSelectPage() {
  const navigate = useNavigate();
  const { state, selectCourse, logout } = useApp();
  const { getCourseProgress } = useProgress();

  const handleSelectCourse = (courseId: string) => {
    selectCourse(courseId);
    navigate(`/course/${courseId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="course-select-page">
      <header className="page-header">
        <div className="brand">
          <FlaskConical size={28} />
          <span>BDP vLab</span>
        </div>
        <div className="user-section">
          <span className="user-name">
            {state.studentInfo?.name || (state.authMode === 'instructor' ? 'Instructor' : 'Student')}
          </span>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="page-content">
        <div className="welcome-section">
          <h1>Welcome, {state.studentInfo?.name || 'Student'}!</h1>
          <p>Select a course to begin your virtual lab experience</p>
        </div>

        <div className="courses-grid">
          {COURSE_LIST.map(course => (
            <CourseCard
              key={course.id}
              course={course}
              progress={getCourseProgress(course.id)}
              onSelect={() => handleSelectCourse(course.id)}
            />
          ))}
        </div>
      </main>

      <style>{`
        .course-select-page {
          min-height: 100vh;
          background: var(--bg-dark, #1A1A1A);
          color: var(--text-primary, #FFFFFF);
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 32px;
          background: var(--bg-surface, #2C2C2C);
          border-bottom: 1px solid var(--border, #4A4A4A);
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .brand {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 20px;
          font-weight: 700;
          color: var(--primary, #FF5A36);
        }
        .user-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .user-name {
          color: var(--text-secondary, #CCCCCC);
          font-size: 14px;
        }
        .logout-btn {
          background: transparent;
          border: 1px solid var(--border, #4A4A4A);
          color: var(--text-secondary, #CCCCCC);
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .logout-btn:hover {
          border-color: var(--danger, #EF4444);
          color: var(--danger, #EF4444);
        }
        .page-content {
          max-width: 900px;
          margin: 0 auto;
          padding: 48px 24px;
        }
        .welcome-section {
          text-align: center;
          margin-bottom: 48px;
        }
        .welcome-section h1 {
          font-size: 32px;
          margin: 0 0 12px 0;
          color: var(--text-primary, #FFFFFF);
        }
        .welcome-section p {
          font-size: 16px;
          color: var(--text-muted, #999999);
          margin: 0;
        }
        .courses-grid {
          display: flex;
          gap: 32px;
          justify-content: center;
          flex-wrap: wrap;
        }
      `}</style>
      <Watermark />
      <CopyrightFooter />
    </div>
  );
}
